// 版本检查和更新提示逻辑

export interface VersionInfo {
  version: string;
  build: number;
  displayVersion: string;
  minVersion: string;
  updateType: 'silent' | 'prompt' | 'force';
  changelog: string | null;
}

const VERSION_KEY = 'enpass_local_version';
const BUILD_KEY = 'enpass_local_build';

// 获取本地保存的版本信息
export function getLocalVersion(): { version: string; build: number } {
  return {
    version: localStorage.getItem(VERSION_KEY) || '0.0.0',
    build: parseInt(localStorage.getItem(BUILD_KEY) || '0', 10),
  };
}

// 保存版本信息到本地
export function saveLocalVersion(version: string, build: number): void {
  localStorage.setItem(VERSION_KEY, version);
  localStorage.setItem(BUILD_KEY, String(build));
}

// 比较版本号 (返回 -1, 0, 1)
function compareVersions(a: string, b: string): number {
  const partsA = a.split('.').map(Number);
  const partsB = b.split('.').map(Number);
  
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const numA = partsA[i] || 0;
    const numB = partsB[i] || 0;
    if (numA < numB) return -1;
    if (numA > numB) return 1;
  }
  return 0;
}

// 检查版本更新
export async function checkForUpdate(): Promise<{
  hasUpdate: boolean;
  updateType: 'silent' | 'prompt' | 'force';
  changelog: string | null;
  remoteVersion: VersionInfo | null;
} | null> {
  try {
    // 添加时间戳防止缓存
    const res = await fetch(`/version.json?t=${Date.now()}`);
    if (!res.ok) {
      console.log('[version] fetch failed:', res.status);
      return null;
    }
    
    const remote: VersionInfo = await res.json();
    const local = getLocalVersion();
    
    console.log('[version] local:', local, 'remote:', remote.version, remote.build);
    
    // 首次安装
    if (local.version === '0.0.0') {
      console.log('[version] first install, saving version');
      saveLocalVersion(remote.version, remote.build);
      return null;
    }
    
    // 检查是否低于最低兼容版本
    if (compareVersions(local.version, remote.minVersion) < 0) {
      return {
        hasUpdate: true,
        updateType: 'force',
        changelog: remote.changelog,
        remoteVersion: remote,
      };
    }
    
    // 检查是否有更新
    const versionDiff = compareVersions(local.version, remote.version);
    const buildDiff = remote.build > local.build;
    
    console.log('[version] versionDiff:', versionDiff, 'buildDiff:', buildDiff, 'updateType:', remote.updateType);
    
    if (versionDiff < 0 || buildDiff) {
      console.log('[version] update available!');
      return {
        hasUpdate: true,
        updateType: remote.updateType,
        changelog: remote.changelog,
        remoteVersion: remote,
      };
    }
    
    console.log('[version] no update needed');
    return { hasUpdate: false, updateType: 'silent', changelog: null, remoteVersion: remote };
  } catch (err) {
    console.warn('[version] check failed:', err);
    return null;
  }
}

// 应用更新（刷新页面）
export function applyUpdate(remoteVersion: VersionInfo): void {
  saveLocalVersion(remoteVersion.version, remoteVersion.build);
  
  // 通知 SW 跳过等待
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage('SKIP_WAITING');
  }
  
  // 稍等一下让 SW 激活，然后刷新
  setTimeout(() => {
    window.location.reload();
  }, 300);
}
